import { defineStore } from "pinia";
import axios from "axios";
import { API_BASE_URL } from "@/config";

// Definiere die User-Typen mit dem Status
export type UserStatus = 'online' | 'away' | 'busy' | 'mobile' | 'offline' | 'custom';

export interface User {
  id: number;
  loginname: string;
  admin: boolean;
  monteur?: boolean;
  lagerist?: boolean;
  developer?: boolean;
  super_admin?: boolean;
  returen_sorting?: boolean;
  returen_sorting_v2?: boolean;
  office?: boolean;
  refurbish?: boolean;
  dtc?: boolean;
  inventur?: boolean;
  vorname?: string;
  nachname?: string;
  user_status?: UserStatus;
  custom_status_message?: string;
}

export const useAuthStore = defineStore("auth", {
  state: () => ({
    user: null as User | null,
    token: null as string | null,
    isAuthenticated: false,
    statusUpdateInterval: null as ReturnType<typeof setInterval> | null,
    lastActivityTime: Date.now(),
    inactivityThreshold: 10 * 60 * 1000, // 10 Minuten in Millisekunden
    autoAwayMode: false, // Neue Variable: Wurde "away" automatisch gesetzt?
    lastStatusUpdate: null as { key: string; time: number } | null, // Debounce für Status-Updates
  }),

  getters: {
    // Status des aktuellen Benutzers
    currentStatus(): UserStatus {
      return this.user?.user_status || 'offline';
    },

    // Benutzerdefinierte Statusnachricht
    currentStatusMessage(): string | undefined {
      return this.user?.custom_status_message;
    },
  },

  actions: {
    async login(loginname: string, passwort: string, rememberMe = false) {
      try {
        const response = await axios.post(
          `${API_BASE_URL}login.php`,
          { loginname, passwort, rememberMe },
          { withCredentials: true }
        );
        if (response.data.status === "success") {
          // Der Token wird ausschließlich im HttpOnly-Cookie gespeichert – hier nur User-Daten speichern:
          this.user = response.data.user;
          this.isAuthenticated = true;

          // Optional: Speichere den Benutzernamen nur, wenn "Angemeldet bleiben" aktiviert ist
          if (rememberMe) {
            localStorage.setItem("lastLogin", loginname);
          } else {
            // Entferne gespeicherten Benutzernamen, wenn "Angemeldet bleiben" nicht aktiviert ist
            localStorage.removeItem("lastLogin");
          }

          // User-Daten immer im LocalStorage speichern für die aktuelle Session
          localStorage.setItem("userData", JSON.stringify(this.user));

          // Status-Updates starten
          this.setupActivityTracking();

          // Wichtig: Kurz warten, bevor das WebSocket-Update gesendet wird
          // um sicherzustellen, dass die Clients vollständig initialisiert sind
          setTimeout(() => {
            this.sendStatusUpdateWebSocket(this.user?.user_status || 'online', this.user?.custom_status_message);
          }, 500);
        } else {
          throw new Error(response.data.message);
        }
      } catch (error) {
        console.error("❌ Login fehlgeschlagen:", error);
        throw new Error("Login fehlgeschlagen!");
      }
    },

    async checkAuth() {
      try {
        const response = await axios.post(
          `${API_BASE_URL}auth_check.php`,
          { action: "check" },
          { withCredentials: true }
        );

        if (response.data.status === "success") {
          // Aktualisiere nur vorhandene Benutzerdaten, nicht den kompletten Benutzer ersetzen
          if (this.user) {
            // Behalte vorhandene Daten und überschreibe nur neue Daten
            this.user = {
              ...this.user,
              ...response.data.user,
              // Stelle sicher, dass ein Status gesetzt ist
              user_status: response.data.user.user_status || this.user.user_status || 'online'
            };
          } else {
            this.user = response.data.user;
          }

          this.isAuthenticated = true;

          // Lokale Speicherung aktualisieren
          localStorage.setItem("userData", JSON.stringify(this.user));

          // Status-Updates starten
          this.setupActivityTracking();

          // Gibt true zurück für erfolgreiche Authentifizierung
          return true;
        } else {
          console.warn("⚠️ Authentifizierung fehlgeschlagen.");
          return false;
        }
      } catch (error) {
        console.error("❌ Fehler bei der Authentifizierung:", error);
        throw error;
      }
    },

    async logout() {
      try {
        // WebSocket-Nachricht über Logout senden, bevor User-Daten zurückgesetzt werden
        if (this.user) {
          this.sendStatusUpdateWebSocket('offline');

          // Kurze Verzögerung, um sicherzustellen, dass die WebSocket-Nachricht gesendet wird
          await new Promise(resolve => setTimeout(resolve, 300));
        }

        await axios.post(
          `${API_BASE_URL}logout.php`,
          {},
          { withCredentials: true }
        );
      } catch (error) {
        console.error("❌ Fehler beim Logout:", error);
      }

      // Status-Updates stoppen
      this.clearActivityTracking();

      // Lokale Daten und Status zurücksetzen
      this.token = null;
      this.user = null;
      this.isAuthenticated = false;

      localStorage.removeItem("userToken");
      localStorage.removeItem("userData");
      document.cookie = "userToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    },

    async restoreAuthentication() {
      try {
        const storedUserData = localStorage.getItem("userData");
        if (storedUserData) {
          // Daten aus dem lokalen Speicher wiederherstellen
          this.user = JSON.parse(storedUserData);
          this.isAuthenticated = true;

          // Sofort eine API-Anfrage senden, um die neuesten Daten abzurufen
          try {
            await this.checkAuth();

            // Explizit WebSocket-Status aktualisieren
            setTimeout(() => {
              // Kurze Verzögerung für WebSocket-Update
              this.sendStatusUpdateWebSocket(this.currentStatus);
            }, 500);
          } catch (error) {
            console.error("Fehler bei checkAuth während restore:", error);
            // Wenn checkAuth fehlschlägt, nutzen wir trotzdem die lokalen Daten
            // Aber wir setzen den Status explizit auf den letzten gespeicherten Status
            if (this.user && !this.user.user_status) {
              this.user.user_status = 'online';
            }
          }

          // Status-Updates starten
          this.setupActivityTracking();
          return true;
        }
        return false;
      } catch (error) {
        console.error("Fehler beim Wiederherstellen der Authentifizierung:", error);
        // Fallback: Status zurücksetzen
        this.resetSession();
        return false;
      }
    },

    // Status-Update Funktionen
// Status-Update Funktionen
async updateUserStatus(status: UserStatus, customMessage?: string, isAuto = false) {
  if (!this.user) return;

  // Verhindere Spam: Wenn derselbe Status bereits gesetzt ist, nichts tun
  if (this.user.user_status === status && this.user.custom_status_message === customMessage) {
    return;
  }

  try {
    // Wenn der Status manuell gesetzt wird oder auf etwas anderes als "away",
    // autoAwayMode entsprechend setzen
    if (isAuto) {
      this.autoAwayMode = true;
    } else {
      this.autoAwayMode = false;
    }

    const response = await axios.post(
      `${API_BASE_URL}updateUserStatus.php`,
      {
        status,
        message: customMessage || null
      },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      if (this.user) {
        this.user.user_status = status;
        this.user.custom_status_message = customMessage;
        // Lokale Speicherung aktualisieren
        localStorage.setItem("userData", JSON.stringify(this.user));

        // WebSocket-Nachricht direkt vom Frontend senden
        this.sendStatusUpdateWebSocket(status, customMessage);
      }
    }
  } catch (error) {
    console.error("❌ Fehler beim Aktualisieren des Status:", error);
  }
},

// Verbesserte WebSocket-Push-Methode - nutzt zentrale WebSocket-Verbindung
async sendStatusUpdateWebSocket(status: UserStatus, customMessage?: string) {
  if (!this.user) return;

  // Debounce: Verhindere mehrfache identische Updates innerhalb 500ms
  const updateKey = `${status}:${customMessage || ''}`;
  const now = Date.now();

  if (this.lastStatusUpdate &&
      this.lastStatusUpdate.key === updateKey &&
      now - this.lastStatusUpdate.time < 500) {
    return;
  }

  this.lastStatusUpdate = { key: updateKey, time: now };

  try {
    // Importiere WebSocketService (ist bereits eine Instanz!)
    const wsService = (await import('@/services/WebSocketService')).default;

    const message = {
      type: 'user_status_update',
      user: {
        id: this.user!.id,
        vorname: this.user?.vorname,
        nachname: this.user?.nachname,
        status: status,
        custom_status_message: customMessage
      }
    };

    // Nutze die zentrale WebSocket-Verbindung
    wsService.send(message);
  } catch (error) {
    console.error("Fehler beim Senden des Status-Updates:", error);
  }
},

    // Aktivitätsverfolgung & Status-Aktualisierung
    setupActivityTracking() {
      // Verhindere mehrfaches Setup - wenn bereits ein Interval läuft, nicht erneut starten
      if (this.statusUpdateInterval) {
        console.warn('⚠️ Activity Tracking bereits aktiv, überspringe Setup');
        return;
      }

      // Event-Listener sicher entfernen falls vorhanden (cleanup vor neuem Setup)
      this.clearActivityTracking();

      // Benutzeraktivität überwachen
      document.addEventListener('mousemove', this.updateActivity);
      document.addEventListener('keydown', this.updateActivity);
      document.addEventListener('click', this.updateActivity);
      document.addEventListener('scroll', this.updateActivity);

      // Regelmäßig den Status und die Aktivität überprüfen, aber nur wenn der Benutzer
      // noch angemeldet ist und die Seite sichtbar ist
      this.statusUpdateInterval = setInterval(() => {
        // Nur ausführen, wenn der Benutzer angemeldet ist und die Seite sichtbar ist
        if (this.isAuthenticated && document.visibilityState === 'visible') {
          this.checkInactivity();

          // Session-Heartbeat immer (auch im autoAwayMode), sonst greift der
          // 5-Minuten-Offline-Sweep obwohl der Tab noch offen ist.
          // forceKeepalive=true → Backend bekommt autoAwayMode:false (kein away→online Flip).
          this.updateActivityBackend(true);
        }
      }, 30000); // Alle 30 Sekunden

      // Sofort beim Einrichten Status setzen
      this.updateActivityBackend();
    },

    clearActivityTracking() {
      if (this.statusUpdateInterval) {
        clearInterval(this.statusUpdateInterval);
        this.statusUpdateInterval = null;
      }

      // Event-Listener entfernen
      document.removeEventListener('mousemove', this.updateActivity);
      document.removeEventListener('keydown', this.updateActivity);
      document.removeEventListener('click', this.updateActivity);
      document.removeEventListener('scroll', this.updateActivity);

      // Stellen Sie sicher, dass kein Aktivitäts-Update mehr versucht wird
      this.lastActivityTime = 0;
    },

    updateActivity() {
      this.lastActivityTime = Date.now();

      // Nur zurücksetzen, wenn der Away-Status automatisch gesetzt wurde
      if (this.user && this.user.user_status === 'away' && this.autoAwayMode) {
        this.updateUserStatus('online');
        this.autoAwayMode = false; // Reset autoAwayMode nach dem Zurücksetzen

        // Backend über die Aktivität informieren (Session-Timer zurücksetzen)
        this.updateActivityBackend();
      }
    },

    async checkInactivity() {
      if (!this.user || !this.isAuthenticated) return;

      const inactiveTime = Date.now() - this.lastActivityTime;

      // Nach Inaktivitätsschwelle automatisch auf "away" setzen
      if (inactiveTime >= this.inactivityThreshold &&
          this.user.user_status === 'online') {
        // Der dritte Parameter "true" bedeutet, dass dieser Status automatisch gesetzt wird
        await this.updateUserStatus('away', undefined, true);
      }
    },

    async updateActivityBackend(forceKeepalive = false) {
      if (!this.user || !this.isAuthenticated) return;

      try {
        // Sende Activity-Update ans Backend (Session-Timer zurücksetzen)
        // forceKeepalive / Interval: autoAwayMode false → kein away→online Flip
        await axios.post(
          `${API_BASE_URL}updateActivity.php`,
          {
            action: "update",
            autoAwayMode: forceKeepalive ? false : this.autoAwayMode
          },
          { withCredentials: true }
        );

        // Keine weitere Aktion nötig - Status-Änderungen werden in updateActivity() gehandhabt
      } catch (error) {
        console.error("❌ Fehler beim Aktualisieren der Aktivität:", error);
      }
    },

    resetSession() {
      // Status-Updates stoppen
      this.clearActivityTracking();

      // Lokale Daten und Status zurücksetzen
      this.token = null;
      this.user = null;
      this.isAuthenticated = false;

      // Entferne alle lokalen Speicherungen
      localStorage.removeItem("userToken");
      localStorage.removeItem("userData");

      // Optional: Versuche das Cookie auch vom Client zu löschen
      document.cookie = "userToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.arcm.alphadystopie.de; secure; SameSite=Lax";
    }
  },
});
